import React, { useState } from "react";
import axios from "axios";
import { X, Camera, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const resizeImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const size = 128;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const min = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const ProfileModal = ({ onClose }) => {
  const { user, checkUserSession } = useAuth();
  const [photo, setPhoto] = useState(user?.photo || "");
  const [name, setName] = useState(user?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPhoto(await resizeImage(file));
    } catch {
      setError("Gagal membaca file gambar");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!name.trim()) { setError("Nama tidak boleh kosong"); return; }
    if (newPassword || confirmPassword || currentPassword) {
      if (!currentPassword) { setError("Isi password saat ini untuk mengganti password"); return; }
      if (newPassword.length < 6) { setError("Password baru minimal 6 karakter"); return; }
      if (newPassword !== confirmPassword) { setError("Konfirmasi password baru tidak sama"); return; }
    }
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/auth/users/${user.id}`, { name: name.trim(), photo }, { withCredentials: true });
      if (newPassword) {
        await axios.post(
          `${BACKEND_URL}/api/auth/change-password`,
          { current_password: currentPassword, new_password: newPassword },
          { withCredentials: true }
        );
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
      await checkUserSession();
      setSuccess("Profil berhasil diperbarui");
      setTimeout(onClose, 900);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Gagal menyimpan profil");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="profile-modal">
      <div className="w-full max-w-sm rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-slate-950/60">
          <h3 className="text-sm font-bold text-white">Profil Saya</h3>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-md" data-testid="profile-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-4">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2" data-testid="profile-error"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{error}</div>}
          {success && <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-2" data-testid="profile-success"><CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />{success}</div>}

          <div className="flex flex-col items-center gap-2">
            <label className="relative cursor-pointer group" data-testid="profile-photo-label">
              <div className="w-20 h-20 rounded-full bg-cyan-600/30 border-2 border-cyan-500/40 overflow-hidden flex items-center justify-center text-cyan-300 font-bold text-2xl uppercase">
                {photo ? <img src={photo} alt="Foto profil" className="w-full h-full object-cover" data-testid="profile-photo-preview" /> : (name?.[0] || "U")}
              </div>
              <span className="absolute bottom-0 right-0 p-1.5 rounded-full bg-cyan-600 text-white shadow-lg group-hover:bg-cyan-500"><Camera className="w-3 h-3" /></span>
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="profile-photo-input" />
            </label>
            <p className="text-[10px] text-slate-500">Klik foto untuk mengganti (otomatis dipotong persegi 128px)</p>
          </div>

          <div>
            <label className="block text-xs text-slate-300 mb-1">Email (tidak bisa diubah)</label>
            <input type="text" value={user?.email || ""} disabled className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-500" data-testid="profile-email" />
          </div>
          <div>
            <label className="block text-xs text-slate-300 mb-1">Nama / Username *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" data-testid="profile-name-input" />
          </div>

          <div className="pt-2 border-t border-slate-800 space-y-3">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ganti Password (opsional)</p>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Password Saat Ini</label>
              <input type={showPass ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" data-testid="profile-current-password" />
            </div>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Password Baru (min. 6)</label>
              <input type={showPass ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" data-testid="profile-new-password" />
            </div>
            <div>
              <label className="block text-xs text-slate-300 mb-1">Konfirmasi Password Baru</label>
              <input type={showPass ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" data-testid="profile-confirm-password" />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
              <input type="checkbox" checked={showPass} onChange={(e) => setShowPass(e.target.checked)} className="accent-cyan-500" data-testid="profile-show-pass" /> Tampilkan password
            </label>
          </div>

          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="profile-save-btn">
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Simpan Perubahan
          </button>
        </form>
      </div>
    </div>
  );
};
